<!-- Loading Overlay for Critical Actions -->
@if (isNavigating() || actionInProgress()) {
  <div class="loading-overlay">
    <div class="loading-overlay-content">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="loading-overlay-text">
        @if (isNavigating()) {
          Navigating...
        } @else if (actionInProgress()) {
          Processing request...
        }
      </div>
    </div>
  </div>
}

<!-- Page Header -->
<div class="page-header">
  <div class="header-content">
    <div class="header-main">
      <div class="header-icon">
        <i class="fa-solid fa-boxes-stacked"></i>
      </div>
      <div class="header-text">
        <h1 class="page-title">Inventory Management</h1>
        <p class="page-description">Manage inventory items, categories, and stock levels</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary"
              (click)="refreshInventory()"
              [disabled]="isAnyActionInProgress()">
        <i class="fa-solid fa-arrows-rotate" [class.fa-spin]="isRefreshing() || isLoadingInventories()"></i>
        @if (isRefreshing()) {
          Refreshing...
        } @else {
          Refresh
        }
      </button>
      <button class="btn btn-export"
              (click)="exportToExcel()"
              [disabled]="isExporting() || !inventories()?.length">
        @if (isExporting()) {
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          Exporting...
        } @else {
          <i class="fa-solid fa-download"></i>
          Export
        }
      </button>
      <button class="btn btn-primary"
              (click)="create()"
              [disabled]="isAnyActionInProgress()">
        @if (isNavigating()) {
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          Loading...
        } @else {
          <i class="fa-solid fa-plus"></i>
          Add Item
        }
      </button>
    </div>
  </div>
</div>

<!-- Filters Panel -->
<div class="filters-panel">
  <div class="search-section">
    <div class="search-box">
      <div class="search-input-wrapper">
        <i class="fa-solid fa-magnifying-glass search-icon" [class.fa-spin]="searchLoading()"></i>
        <input type="text"
               class="search-input"
               placeholder="Search by name, category, or description..."
               [(ngModel)]="search"
               (input)="onSearch()"
               [disabled]="isFilteringCategory() || isFilteringStatus() || isClearingFilters()">
        @if (search) {
          <button class="clear-search"
                  (click)="search = ''; onSearch()"
                  [disabled]="searchLoading()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        }
      </div>
    </div>
  </div>

  <div class="filters-section">
    <div class="filter-group">
      <!-- Category Filter -->
      <div ngbDropdown class="filter-dropdown">
        <button type="button"
                class="btn btn-filter"
                ngbDropdownToggle
                [disabled]="isAnyActionInProgress()">
          @if (isFilteringCategory()) {
            <div class="spinner-border spinner-border-sm me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            Filtering...
          } @else {
            <i class="fa-solid fa-tag me-2"></i>
            {{ selectedCategory || 'All Categories' }}
            <i class="fa-solid fa-chevron-down ms-2"></i>
          }
        </button>
        <div ngbDropdownMenu class="filter-menu">
          <button ngbDropdownItem (click)="clearFilter()">
            <i class="fa-solid fa-layer-group me-2"></i>All Categories
          </button>
          @if (isLoadingCategories()) {
            <div class="dropdown-item-text text-center py-3">
              <div class="spinner-border spinner-border-sm me-2" role="status"></div>
              Loading categories...
            </div>
          } @else {
            @for (item of listcategories; track item.id) {
              <button ngbDropdownItem (click)="CatFilter(item.name, item.id)">
                <i class="fa-solid fa-tag me-2"></i>{{ item.name }}
              </button>
            }
            @empty {
              <div class="dropdown-item-text text-muted">No categories available</div>
            }
          }
        </div>
      </div>

      <!-- Status Filter -->
      <div ngbDropdown class="filter-dropdown">
        <button type="button"
                class="btn btn-filter"
                ngbDropdownToggle
                [disabled]="isAnyActionInProgress()">
          @if (isFilteringStatus()) {
            <div class="spinner-border spinner-border-sm me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            Filtering...
          } @else {
            <i class="fa-solid fa-circle-half-stroke me-2"></i>
            {{ capitalizeFirstLetter(selectedStatus) || 'All Status' }}
            <i class="fa-solid fa-chevron-down ms-2"></i>
          }
        </button>
        <div ngbDropdownMenu class="filter-menu">
          <button ngbDropdownItem (click)="clearFilter()">
            <i class="fa-solid fa-circle me-2"></i>All Status
          </button>
          <button ngbDropdownItem (click)="statuusFilter('active')">
            <i class="fa-solid fa-circle-check me-2 text-success"></i>Active
          </button>
          <button ngbDropdownItem (click)="statuusFilter('inactive')">
            <i class="fa-solid fa-circle-xmark me-2 text-danger"></i>Inactive
          </button>
        </div>
      </div>

      <!-- Clear Filters -->
      @if (selectedCategory || selectedStatus || search) {
        <button class="btn btn-clear-filters"
                (click)="clearFilter()"
                [disabled]="isAnyActionInProgress()">
          @if (isClearingFilters()) {
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            Clearing...
          } @else {
            <i class="fa-solid fa-xmark"></i>
            Clear Filters
          }
        </button>
      }
    </div>
  </div>

  <div class="stats-section">
    <div class="stat-card">
      <div class="stat-icon">
        <i class="fa-solid fa-cubes"></i>
      </div>
      <div class="stat-content">
        <div class="stat-number">{{ inventories()?.length || 0 }}</div>
        <div class="stat-label">Items Found</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon total">
        <i class="fa-solid fa-database"></i>
      </div>
      <div class="stat-content">
        <div class="stat-number">{{ size || 0 }}</div>
        <div class="stat-label">Total Items</div>
      </div>
    </div>
  </div>
</div>

<!-- Data Table Panel -->
<div class="data-panel">
  <div class="panel-header">
    <div class="panel-title">
      <div class="title-icon">
        <i class="fa-solid fa-table-list"></i>
      </div>
      <div class="title-content">
        <div class="title-text">Inventory Directory</div>
        <div class="title-subtitle">Complete list of inventory items with details and status</div>
      </div>
    </div>
    @if (!isLoadingInventories()) {
      <div class="panel-info">
        <div class="info-item">
          <span class="info-label">Page:</span>
          <span class="info-value">{{ page }} of {{ getTotalPages() }}</span>
        </div>
      </div>
    }
  </div>

  <div class="panel-content">
    @if (isLoadingInventories()) {
      <!-- Loading State -->
      <div class="loading-state">
        <div class="loading-content">
          <div class="loading-spinner">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div class="loading-text">
            <div class="loading-title">Loading Inventory Items</div>
            <div class="loading-subtitle">Please wait while we fetch the latest data...</div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Table -->
      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th style="width: 70px;">Image</th>
              <th>Item Details</th>
              <th>Category</th>
              <th>Quantity</th>
              <th>Pricing</th>
              <th>Status</th>
              <th style="width: 80px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for(inv of inventories(); track inv.id) {
              <tr>
                <td>
                  <img [src]="inv?.main_image || 'profile.png'"
                       [alt]="inv?.name"
                       style="width: 44px; height: 44px; border-radius: var(--radius-md); object-fit: cover; border: 1px solid var(--border);">
                </td>
                <td>
                  <div style="font-weight: 600; color: var(--text-primary); font-size: 13px;">{{ inv?.name }}</div>
                  @if (inv?.description) {
                    <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">{{ inv?.description | slice:0:50 }}@if(inv?.description?.length > 50){...}</div>
                  }
                </td>
                <td>
                  <span class="badge badge-soft-primary badge-dot">{{ inv?.category?.name }}</span>
                </td>
                <td>
                  @if (inv?.quantity < 5) {
                    <span class="badge badge-soft-danger badge-dot">{{ inv?.quantity }}</span>
                    <div style="font-size: 11px; color: var(--danger-text); margin-top: 3px;">Critical Stock</div>
                  } @else if (inv?.quantity < 10) {
                    <span class="badge badge-soft-warning badge-dot">{{ inv?.quantity }}</span>
                    <div style="font-size: 11px; color: var(--warning-text); margin-top: 3px;">Low Stock</div>
                  } @else {
                    <span class="badge badge-soft-success badge-dot">{{ inv?.quantity }}</span>
                  }
                </td>
                <td>
                  <div style="font-size: 12px; color: var(--text-tertiary);">Cost: <span style="font-weight: 600; color: var(--text-secondary);">{{ inv?.cost_price | currency: '₦' }}</span></div>
                  <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">Sell: <span style="font-weight: 600; color: var(--accent);">{{ inv?.selling_price | currency: '₦' }}</span></div>
                </td>
                <td>
                  @if (inv.disabled == false) {
                    <span class="badge badge-status-active badge-dot">Active</span>
                  } @else {
                    <span class="badge badge-status-inactive badge-dot">Disabled</span>
                  }
                </td>
                <td>
                  <div ngbDropdown container="body" display="dynamic">
                    <button class="btn btn-sm btn-outline-secondary"
                            ngbDropdownToggle
                            [disabled]="isAnyActionInProgress()"
                            style="border: none; padding: 6px 10px;">
                      @if (isTogglingStatus() || actionInProgress()) {
                        <div class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      } @else {
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      }
                    </button>
                    <div ngbDropdownMenu>
                      <button ngbDropdownItem
                              (click)="edit(inv?.id)"
                              [disabled]="isAnyActionInProgress()">
                        <i class="fa-solid fa-pen-to-square me-2" style="color: var(--info-text);"></i>
                        Edit Item
                      </button>
                      <button ngbDropdownItem
                              (click)="viewLogs(inv)"
                              [disabled]="isAnyActionInProgress()">
                        <i class="fa-solid fa-clock-rotate-left me-2" style="color: var(--accent);"></i>
                        View Stock
                      </button>
                      <div class="dropdown-divider"></div>
                      @if (inv.disabled == true) {
                        <button ngbDropdownItem
                                (click)="enable(inv)"
                                [disabled]="isAnyActionInProgress()">
                          <i class="fa-solid fa-circle-check me-2" style="color: var(--success-text);"></i>
                          Enable Item
                        </button>
                      } @else {
                        <button ngbDropdownItem
                                (click)="enable(inv)"
                                [disabled]="isAnyActionInProgress()">
                          <i class="fa-solid fa-ban me-2" style="color: var(--danger-text);"></i>
                          Disable Item
                        </button>
                      }
                    </div>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="7">
                  <div class="empty-state">
                    <div class="empty-icon">
                      <i class="fa-solid fa-boxes-stacked" style="font-size: 40px;"></i>
                    </div>
                    <div class="empty-content">
                      <h5>No Inventory Items Found</h5>
                      <p>
                        @if (search || selectedCategory || selectedStatus) {
                          No items match your current filters. Try adjusting your search criteria.
                        } @else {
                          Start by adding your first inventory item to get started.
                        }
                      </p>
                      <div>
                        @if (!search && !selectedCategory && !selectedStatus) {
                          <button class="btn btn-primary"
                                  (click)="create()"
                                  [disabled]="isNavigating()">
                            @if (isNavigating()) {
                              <div class="spinner-border spinner-border-sm me-2" role="status">
                                <span class="visually-hidden">Loading...</span>
                              </div>
                              Loading...
                            } @else {
                              <i class="fa-solid fa-plus me-2"></i>
                              Add First Item
                            }
                          </button>
                        } @else {
                          <button class="btn btn-outline-secondary"
                                  (click)="clearFilter()"
                                  [disabled]="isClearingFilters()">
                            @if (isClearingFilters()) {
                              <div class="spinner-border spinner-border-sm me-2" role="status">
                                <span class="visually-hidden">Loading...</span>
                              </div>
                              Clearing...
                            } @else {
                              <i class="fa-solid fa-xmark me-2"></i>
                              Clear Filters
                            }
                          </button>
                        }
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>

  <!-- Pagination Footer -->
  @if (size > pageSize && !isLoadingInventories()) {
    <div class="panel-footer">
      <div class="pagination-info">
        <div class="info-text">
          Showing {{ ((page - 1) * pageSize) + 1 }} to {{ Math.min(page * pageSize, size) }} of {{ size }} entries
        </div>
      </div>
      <div class="pagination-controls">
        <ngb-pagination
          [collectionSize]="size"
          [(page)]="page"
          [pageSize]="pageSize"
          [maxSize]="5"
          [rotate]="true"
          [ellipses]="false"
          [boundaryLinks]="true"
          (pageChange)="refresh($event)"
          size="sm"
        />
      </div>
    </div>
  }
</div>
